import "gridstack/dist/gridstack.min.css";
import { GridStack } from "gridstack";

import { CollectionView } from "backbone.marionette";
import { _ } from "extensions/underscore";
import $ from "jquery";

import { PazienCollection } from "common/collections/pazienCollection";
import { store } from "common/store";

import loader from "common/helpers/horizontal-loader.jst";

import { getByType } from "./widgets/_all";
import { WidgetsCollection } from "./widgetsCollection";

export const DashboardContent = CollectionView.extend({
    className: "widgets grid-stack",
    childView: (widgetModel) => getByType(widgetModel.get("widgetType"), "View"),
    
    getViewCollection({ url, params = {}, mode = "client" }) {
        const hash = `${url}_${JSON.stringify(params)}`;
        let isNew = true;
        // Check if we already have a reference to a collection with the same URL
        // it's much faster to reuse the same data-collection than create a new one and bombard the API
        if (Object.prototype.hasOwnProperty.call(this.collections, hash)) {
            // We don't want to arm collection multiple times to react on global pickers
            isNew = false;
            // need to refresh this back to original since each widget is modifying temporarily
            // ..either that or clone the whole collection
            this.collections[hash].setQualifiers(this.getOption("qualifiers"));
        } else {
            this.collections[hash] = new PazienCollection();
            this.collections[hash].url = url;
            
            // Don't set limit & page params to dashboard widgets
            this.collections[hash].getAllParameters = function() {
                return this.queryParams;
            };
    
            if (Object.keys(params).length > 0) {
                this.collections[hash].setQueryParam(params);
            }
            // Set filter qualifiers
            this.collections[hash].setQualifiers(this.getOption("qualifiers"));
        }
        
        return {
            collection: this.collections[hash],
            isNew
        };
    },
    
    initialize() {
        this.trackingRequests = [];
        this.grid = null;
        this.childViewOptions = {
            parent: this
        };
        this.collections = {};
        
        this.collection = new WidgetsCollection();
        this.listenTo(this.collection, "fetched", () => {
            this.resetWidgetCounts();
            this.removeLoader();
            this.initGridstack();
        });
        this.collection.fetch();

        this.scrollHandler = _.throttle(this.scrollHandler.bind(this), 100);
    },
    
    resetWidgetCounts() {
        // Set number of total dashboard widgets
        this.totalWidgets = this.collection.length;
        
        // Reset number of loaded widgets
        this.loadedWidgets = 0;
    },
    
    getChildByCid(cid) {
        return this.children.findByCid(cid);
    },
    
    addLoader() {
        this.$el.append(loader());
    },
    
    removeLoader() {
        this.$el.children(".loading-spinner-wrapper").remove();
    },
    
    withChild(fn) {
        return (event, widgetEl) => {
            if (!widgetEl) return;
            
            const child = this.children.findByCid(widgetEl.dataset.cid);
            
            if (child) {
                fn(child);
            }
        };
    },

    hasLazyLoad() {
        const state = store.getState();
        return state.common.user.superuser && state.settings.dashboardLazyLoad;
    },

    scrollHandler() {
        if (this.offScreenWidgets?.length) {
            this.offScreenWidgets = this.offScreenWidgets.filter((child) => {
                if (child.top <= window.scrollY + window.innerHeight) {
                    child.triggerMethod("gridstack:init");
                    return false;
                }
                
                return true;
            });
        } else {
            window.removeEventListener("scroll", this.scrollHandler);
        }
    },
    
    initGridstack() {
        this.grid = GridStack.init({
            cellHeight: 105,
            disableOneColumnMode: true,
            minWidth: 1000,
            margin: 8,
            draggable: {
                handle: ".drag-grip",
                scroll: true
            }
        }, this.el);
        
        const events = {
            dragstart: this.withChild((child) => {
                child.isDragging = true;
            }),
            
            dragstop: this.withChild((child) => {
                window.setTimeout(() => {
                    child.isDragging = false;
                }, 100);
            }),
    
            resizestart: this.withChild((child) => {
                child.isResizing = true;
            }),
    
            resizestop: this.withChild((child) => {
                // Don't falsyfy isResizing immediately to prevent drilldowns
                window.setTimeout(() => {
                    child.reflowWidget();
                    child.isResizing = false;
                }, 100);
            }),
            
            change: () => {
                this.children.each((child) => {
                    child.model.set({
                        x: child.el.getAttribute("gs-x"),
                        y: child.el.getAttribute("gs-y"),
                        w: child.el.getAttribute("gs-w"),
                        h: child.el.getAttribute("gs-h")
                    });
                });
                this.storeUserDashboard();
            }
        };
    
        Object.entries(events).forEach(([event, handler]) => this.grid.on(event, handler));
        this.offScreenWidgets = [];

        this.children.each((child) => {
            if (!child.isDestroyed() && child.isRendered()) {
                const { top } = child.el.getBoundingClientRect();
                // Set a reference to main grid to all children
                child.grid = this.grid;
                child.top = top;

                if (!this.hasLazyLoad()) {
                    child.triggerMethod("gridstack:init");
                    return;
                }

                if (top <= window.scrollY + window.innerHeight) {
                    child.triggerMethod("gridstack:init");
                } else {
                    this.offScreenWidgets.push(child);
                }
            }
        });

        if (this.hasLazyLoad()) window.addEventListener("scroll", this.scrollHandler);
    },
    
    onRender() {
        this.addLoader();
    },
    
    onBeforeDestroy() {
        if (this.grid) {
            this.$el.off(".gridstack");
            this.grid.destroy();
            $(window).off("resize.gridstack");
        }
        
        this.collections = null;

        window.removeEventListener("scroll", this.scrollHandler);
    },
    
    storeUserDashboard: _.debounce(function() {
        this.collection.save();
    }, 1000)
});
